import { CloudOff, Download, Edit3, ImagePlus, LoaderCircle, LockKeyhole, MessageCircle, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { FullCompositionViewer } from '../../components/FullCompositionViewer';
import { Button } from '../../components/Button';
import { ScreenHeader } from '../../components/ScreenHeader';
import { StepProgress } from '../../components/StepProgress';
import { useI18n } from '../../i18n/I18nProvider';
import type { TryonSyncState } from '../../api/client';
import { useCapability } from '../capabilities/Capability';

interface Props {
  previewUrl: string;
  preparing?: boolean;
  prepareError?: string;
  onPrepareRetry?: () => void;
  unlocked: boolean;
  allowCleanDownload: boolean;
  syncState: TryonSyncState;
  busy?: boolean;
  downloadBusy?: boolean;
  error?: string;
  onBack: () => void;
  onBooking: () => void;
  onRetry: () => void;
  onEdit: () => void;
  onTryAnother: () => void;
  onSaveFile: () => void;
}

export function ResultScreen({ previewUrl, preparing, prepareError, onPrepareRetry, unlocked, allowCleanDownload, syncState, busy, downloadBusy, error, onBack, onBooking, onRetry, onEdit, onTryAnother, onSaveFile }: Props) {
  const { t } = useI18n();
  const booking = useCapability('booking.enabled');
  const cleanDownloads = useCapability('downloads.clean');
  const [capabilityNotice, setCapabilityNotice] = useState('');
  const canDownload = allowCleanDownload || unlocked;
  return (
    <main className="screen result-screen">
      <ScreenHeader title={t('result.title')} onBack={onBack} />
      <div className="screen-content result-content">
        <StepProgress current={2} />
        {preparing || prepareError ? <div className="result-preparing" role={prepareError ? 'alert' : 'status'} aria-busy={preparing}>{preparing ? <><span className="spinner" /><h1>{t('result.preparing')}</h1></> : <><p>{prepareError}</p><Button onClick={onPrepareRetry}>{t('common.retry')}</Button></>}</div> : <>
        <div className="result-heading"><p className="eyebrow">{t('result.ready')}</p><h1>{t('result.heading')}</h1><p>{t('result.copy')}</p></div>
        <FullCompositionViewer className="result-card" src={previewUrl} alt={t('result.alt')}><ShieldCheck /> {t('result.privacy')}</FullCompositionViewer>
        {syncState !== 'synced' && <div className={`result-sync result-sync--${syncState}`} role="status">
          {syncState === 'syncing' ? <LoaderCircle className="spinner" /> : <CloudOff />}
          <span>{t(syncState === 'syncing' ? 'result.saving' : syncState === 'local_only' ? 'result.localOnly' : 'result.syncFailed')}</span>
          {syncState === 'sync_failed' && <Button variant="ghost" busy={busy} onClick={onRetry}>{t('common.retry')}</Button>}
        </div>}
        {error && <p className="panel-note" role={syncState === 'sync_failed' ? 'alert' : 'status'}>{error}</p>}
        {booking.isEnabled ? <Button block icon={<MessageCircle />} onClick={onBooking} busy={busy}>{t('result.booking')}</Button> : booking.isLocked ? <><Button block variant="secondary" icon={<MessageCircle />} aria-disabled="true" onClick={() => setCapabilityNotice(t('capability.otherPlan'))}>{t('result.booking')} ★</Button>{capabilityNotice && <p className="capability-inline-note" role="status">{capabilityNotice}</p>}</> : null}
        <div className="result-secondary-actions"><Button variant="secondary" icon={<Edit3 />} onClick={onEdit}>{t('result.edit')}</Button><Button variant="ghost" icon={<ImagePlus />} onClick={onTryAnother}>{t('result.another')}</Button></div>
        {!cleanDownloads.isHidden && <section className={`unlock-card${canDownload ? ' is-unlocked' : ''}`}>
          <div className="unlock-card__icon">{canDownload ? <Download /> : <LockKeyhole />}</div>
          <div><strong>{t(canDownload ? 'result.clean' : 'result.cleanHd')}{cleanDownloads.isLocked ? ' ★' : ''}</strong><p>{t(canDownload ? 'result.cleanCopy' : 'result.disabledCopy')}</p></div>
          <Button variant={canDownload ? 'primary' : 'secondary'} onClick={onSaveFile} busy={downloadBusy} disabled={!canDownload}>{t(downloadBusy ? 'common.downloading' : canDownload ? 'common.download' : 'result.unavailable')}</Button>
        </section>}
        </>}
      </div>
    </main>
  );
}
